/**
 * Retouren v3 (sessie 172): lijst op aandacht en het dossier als held.
 *
 * Het contract staat in docs/werkplek/RETOUREN-V3.md; deze types volgen
 * het letterlijk. Alleen de lead wijzigt dit bestand.
 */
import { apiClient } from "@/lib/api";

export type RetourV3Filter = "aandacht" | "loopt" | "alles";
export type RetourV3Groep = "besluit" | "loopt" | "afgerond";

export type RetourV3StandCode =
  | "picqer_niet_aangemeld"
  | "binnen_besluit"
  | "beoordeling"
  | "klaargezet"
  | "akkoord_gegeven"
  | "label_mislukt"
  | "label_wacht"
  | "geen_label"
  | "onderweg"
  | "herinnerd"
  | "terugbetaald"
  | "afgewezen"
  | "verlopen"
  | "afgehandeld"
  | "niet_aangenomen"
  | "geannuleerd";

export interface RetourV3Regel {
  soort: "retour" | "bundel";
  sleutel: string;
  retour_id: string | null;
  zaak_id: string | null;
  naam: string;
  product: string;
  stand_code: RetourV3StandCode;
  stand_zin: string;
  bedrag_cents: number | null;
  retournummer: string | null;
  leden: RetourV3Regel[];
}

export interface RetourV3LijstGroep {
  sleutel: RetourV3Groep;
  titel: string;
  regels: RetourV3Regel[];
}

export interface RetourV3Lijst {
  filter: RetourV3Filter;
  zoek: string | null;
  aantal_besluit: number;
  groepen: RetourV3LijstGroep[];
  aantal_afgerond: number;
  picqer_gecontroleerd_op: string | null;
}

export type RetourV3StapStand = "gedaan" | "nu" | "nog_niet" | "eind";

export interface RetourV3Stap {
  sleutel: string;
  label: string;
  kort: string;
  datum: string;
  stand: RetourV3StapStand;
}

export interface RetourV3Verzendkosten {
  bedrag_cents: number;
  voor: "klant" | "voeronline" | "onbekend";
  omzetbaar: boolean;
  omgezet_door: string | null;
  omgezet_op: string | null;
  /** Sessie 186: false als de regel "onbekend voor wie" niets zegt (afgehandeld). */
  tonen?: boolean;
  /** Sessie 186: tweede keuze "ook de heenverzending terug" (alleen bij een hele bestelling). */
  heenverzending?: {
    bedrag_cents: number;
    aan: boolean;
    /** Herstel 186: onwaar bij een open klaarzetting, na akkoord of na terugbetaling. */
    kan_wijzigen?: boolean;
    /** Waarom het vinkje er niet staat; leeg als het kan. */
    reden?: string | null;
  } | null;
}

export interface RetourV3Afspraak {
  id: string;
  tekst: string;
  termijn: string | null;
}

export interface RetourV3Feiten {
  aangemeld: string;
  ontvangen: string;
  aangemeld_en_ontvangen: string;
  reden: string;
  verzendkosten: RetourV3Verzendkosten;
  afspraken: RetourV3Afspraak[];
  terug: RetourV3Terug;
}

/** Geldstand van de retour (sessie 186, ``retour_v3_stand.bepaal_geld``). */
export interface RetourV3Terug {
  totaal_cents: number;
  rekensom: string;
  code?: string;
  label?: string;
  toon_bedrag?: boolean;
  zin?: string;
}

export type RetourV3HoofdSleutel =
  | "terugbetalen_klaarzetten"
  | "akkoord"
  | "koppel_klant"
  | "label_opnieuw"
  | "label_maken"
  | "heropenen";

export type RetourV3KnopSleutel =
  | "label_opnieuw"
  | "afwijzen"
  | "nasturen"
  | "kortingscode"
  | "case_vervoerder"
  | "melding_leverancier"
  | "pakbon";

export interface RetourV3Hoofdknop {
  sleutel: RetourV3HoofdSleutel;
  label: string;
  na_akkoord: string;
  actie_id: string | null;
  /** Bij akkoord: de mail die na de klik naar de klant gaat. */
  mail_onderwerp?: string | null;
  mail_tekst?: string | null;
}

export interface RetourV3Knoppen {
  hoofd: RetourV3Hoofdknop | null;
  naast: RetourV3KnopSleutel[];
  meer: RetourV3KnopSleutel[];
}

export interface RetourV3Contact {
  datum: string;
  moment: string | null;
  kanaal: string;
  zin: string;
}

export interface RetourV3KlantVoorstel {
  klant_id: string;
  naam: string;
  bron: "order" | "adres";
  open_zaken: { zaak_id: string; onderwerp: string; laatst: string }[];
}

export interface RetourV3Dossier {
  retour_id: string;
  retournummer: string;
  zaak_id: string | null;
  klant_id: string;
  klant_naam: string | null;
  kopregel: string;
  kopregel_kort: string;
  klant_telefoon: string | null;
  klant_email: string | null;
  lightspeed_order_url: string | null;
  ordernummer: string | null;
  stand_code: RetourV3StandCode;
  stand_zin: string;
  groep: RetourV3Groep;
  tijdlijn: RetourV3Stap[];
  feiten: RetourV3Feiten;
  knoppen: RetourV3Knoppen;
  contacten: RetourV3Contact[];
  klant_voorstel: RetourV3KlantVoorstel | null;
}

const BASIS = "/api/werkplek/retouren-v3";

export const retourenV3Api = {
  /** Tweede keuze "ook de heenverzending terug" (besluit 19-10, sessie 186). */
  async heenverzendingTerug(retourId: string, aan: boolean): Promise<RetourV3Dossier> {
    const { data } = await apiClient.post<RetourV3Dossier>(
      `${BASIS}/${encodeURIComponent(retourId)}/heenverzending-terug`,
      { aan },
    );
    return data;
  },
  /** Afgerond buiten het portaal (sessie 186): geen mail, geen label, geen geldketen. */
  async afgerondBuitenPortaal(
    retourId: string,
    invoer: { reden: string; toelichting: string | null },
  ): Promise<RetourV3Dossier> {
    const { data } = await apiClient.post<RetourV3Dossier>(
      `${BASIS}/${encodeURIComponent(retourId)}/afgerond-buiten-portaal`,
      invoer,
    );
    return data;
  },
  async lijst(params: { filter: RetourV3Filter; zoek?: string }): Promise<RetourV3Lijst> {
    const { data } = await apiClient.get<RetourV3Lijst>(BASIS, {
      params: { filter: params.filter, zoek: params.zoek || undefined },
    });
    return data;
  },

  async dossier(retourId: string): Promise<RetourV3Dossier> {
    const { data } = await apiClient.get<RetourV3Dossier>(`${BASIS}/${retourId}`);
    return data;
  },

  async koppelKlant(
    retourId: string,
    invoer: { klant_id: string; zaak_id: string | null },
  ): Promise<RetourV3Dossier> {
    const { data } = await apiClient.post<RetourV3Dossier>(
      `${BASIS}/${retourId}/koppel-klant`,
      invoer,
    );
    return data;
  },

  async verzendkostenNaarOns(retourId: string): Promise<RetourV3Dossier> {
    const { data } = await apiClient.post<RetourV3Dossier>(
      `${BASIS}/${retourId}/verzendkosten`,
      { voor: "voeronline" },
    );
    return data;
  },

  async afspraak(
    retourId: string,
    invoer: { tekst: string; termijn_op: string | null },
  ): Promise<RetourV3Dossier> {
    const { data } = await apiClient.post<RetourV3Dossier>(
      `${BASIS}/${retourId}/afspraak`,
      invoer,
    );
    return data;
  },

  async klaarzetten(
    retourId: string,
    invoer: { oplossing: "crediteren_volledig" | "afgewezen"; reden: string | null },
  ): Promise<RetourV3Dossier> {
    const { data } = await apiClient.post<RetourV3Dossier>(
      `${BASIS}/${retourId}/klaarzetten`,
      invoer,
    );
    return data;
  },

  async akkoord(retourId: string, actieId: string): Promise<RetourV3Dossier> {
    const { data } = await apiClient.post<RetourV3Dossier>(`${BASIS}/${retourId}/akkoord`, {
      actie_id: actieId,
    });
    return data;
  },

  async annuleerKlaarzetten(retourId: string, actieId: string): Promise<RetourV3Dossier> {
    const { data } = await apiClient.post<RetourV3Dossier>(
      `${BASIS}/${retourId}/klaarzetten/annuleer`,
      { actie_id: actieId },
    );
    return data;
  },
};
